import {makeModal} from "../lib/ams-modal";

function setupSelectAllBehaviour($container) {
    // Set up the select all behaviour...
    const $selectAll = $container.querySelectorAll('table thead [type=checkbox][value=all]');
    $selectAll.forEach($allCheckbox => {
        $allCheckbox.addEventListener('change', () => {
            $container.querySelectorAll('table tbody [type=checkbox]')
                .forEach($checkbox => {
                    $checkbox.checked = $allCheckbox.checked;
                });
        });
    });
}

function setupBulkActionBehaviour($container) {
    const $bulkActions = $container.querySelectorAll('.ams-cpd-control-group--bulk-actions');

    if (!$bulkActions.length) {
        return;
    }

    const disableActions = () => {
        $bulkActions.forEach($actionContainer => {
            $actionContainer.querySelectorAll('button, input, select, textarea').forEach($button => {
                $button.disabled = true;
            });
        });
    }

    const enableActions = () => {
        $bulkActions.forEach($actionContainer => {
            $actionContainer.querySelectorAll('button, input, select, textarea').forEach($button => {
                $button.disabled = false;
            });
        });
    }

    // Toggle action enabled/disabled based on checkbox state.
    $container.addEventListener('change', event => {
        // Check for a selected checkbox, and enable/disable accordingly.
        if ($container.querySelector('[type=checkbox]:checked')) {
            enableActions();
        } else {
            disableActions();
        }
    });

    // Disable buttons when clicked.
    $bulkActions.forEach($actionContainer => {
        $actionContainer.addEventListener('click', event => {
            // If it's not a button, bail.
            if (!event.target.closest('button')) {
                return;
            }

            // Disable on next tick, so the event still triggers form submit...
            window.setTimeout(() => {
                $actionContainer.querySelectorAll('button').forEach($button => {
                    $button.disabled = true;
                });
            });
        });
    });

    disableActions();
}

/**
 * Given a container, setup the row edit behaviour, if it's right to do so...
 *
 * @param $container The container.
 */
function setupRowEditBehaviour($container) {
    const $editButtons = $container.querySelectorAll('[data-ams-cpd-entry-property="edit"] button');

    if (!$editButtons.length) {
        return;
    }

    $editButtons.forEach($button => {
        $button.addEventListener('click', event => {
            event.preventDefault();
            event.stopImmediatePropagation();

            const $row = event.target.closest('[data-ams-cpd-entry-id]');
            const entryId = parseInt($row?.dataset?.amsCpdEntryId);

            if (!entryId) {
                alert('Could not find entry ID.');
                return;
            }

            showEntryEditor(entryId);
        });
    })
}

/**
 * Pass in an ID, or use 0 for creating a new entry.
 * @param entryId
 */
function showEntryEditor(entryId) {
    if (!jQuery) {
        alert('jQuery not loaded.');
        return;
    }

    const fetchEditFormHtmlURL = window.ams_cpd_entries_manager?.entry_edit_form_url;
    const updateEntryURL = window.ams_cpd_entries_manager?.entry_update_url;
    const userSearchURL = window.ams_cpd_entries_manager?.user_search_url;

    if (!fetchEditFormHtmlURL) {
        alert(`No edit form URL provided, something went wrong! Try reloading, and if that doesn't work, contact support.`);
        return;
    }
    // Create our modal.
    const modal = makeModal();

    modal.setTitle('Edit CPD Entry');
    modal.setDescription(entryId
        ? `Editing entry #${entryId}.`
        : 'Creating new entry'
    );
    modal.setBusy(true);
    modal.open();

    // Capture form submit.
    modal.$dialog.addEventListener('submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
    });

    const $saveButton = document.createElement('button');
    $saveButton.innerText = 'Save';
    $saveButton.classList.add('button', 'button-primary');
    $saveButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const $form = modal.$dialog.querySelector('form');

        // Trigger validation and bail if it was invalid...
        if (!$form.reportValidity()) {
            return;
        }

        modal.setBusy(true);

        // Get form data from the modal.
        const formData = new FormData($form);

        // Add the entry ID.
        formData.append('entry_id', entryId);

        const data = Object.fromEntries(formData);

        // Submit the form.
        jQuery.post(updateEntryURL, data)
            .done(result => {
                modal.setContent(result?.data?.message || 'Success, but no message returned.');
                // Keep the modal open and busy, but trigger a window reload.
                window.location.reload();
            })
            .fail(error => {
                console.error('Form submission error', {error});
                alert(error?.responseJSON?.data?.error || 'Error, please try again.');
                modal.setBusy(false);
            });
    });

    const $cancelButton = document.createElement('button');
    $cancelButton.innerText = 'Cancel';
    $cancelButton.classList.add('button');
    $cancelButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        modal.close();
    });

    modal.$actions.appendChild($cancelButton);

    // Setup the user search behaviour. It expects the elements to be in a certain order and relation
    // to the triggering search field. This ensures we are in the right place and doing the right thing.
    // When a search is performed, a list of email=>IDs are stored, and if the input ever matches that
    // then the hidden _actual_ id input is set to that value. This means we get the nice search UI with
    // a freeform text box using native elements.
    let _currentSearchResults = {};
    modal.$content.addEventListener('input', event => {
        console.log('input event', event);
        /** @type {HTMLInputElement} */
        const $target = event.target;

        if ($target.getAttribute('id') !== 'cpd_user_id_search') {
            return;
        }

        const $datalist = $target.nextElementSibling;
        if (!$datalist || $datalist.tagName !== 'DATALIST') {
            console.warn('Had a user search input, however there was no data list!');
            return;
        }

        const $userIdField = $datalist.nextElementSibling;
        if (!$userIdField || $userIdField.tagName !== 'INPUT' || $userIdField.getAttribute('id') !== 'cpd_user_id') {
            console.warn('Had a user search input, however there was no user id field!!');
            return;
        }

        console.log('search for user with', $target.value);

        const searchString = $target.value.trim();

        if (!searchString || searchString.length < 3) {
            return;
        }

        if (_currentSearchResults[searchString]) {
            console.info('set result to', searchString, _currentSearchResults[searchString]);
            $userIdField.setAttribute('value', _currentSearchResults[searchString]);
            return;
        }

        // Clear out the value as we're searching...
        $userIdField.setAttribute('value', '');

        window.clearTimeout($target.__userLookupDebounce);
        const $label = $target.closest('.ams-cpd-entry-edit-field')?.querySelector('label');
        $label?.classList?.add('busy');
        $target.__userLookupDebounce = window.setTimeout(() => {
            // Submit the form.
            jQuery.post(userSearchURL, {search: $target.value})
                .done(result => {
                    console.log('user search result', result);
                    const users = result.data?.users || [];

                    // Reset the current search results for easy matching...
                    _currentSearchResults = {};

                    // Map the user list to option elements, while populating the existing results...
                    const userOptions = users.map(user => {
                        _currentSearchResults[user.email] = user.ID;
                        return `<option value="${user.email}">${user.label}</option>`;
                    }).join('');

                    // Set the datalist options...
                    $datalist.innerHTML = userOptions;
                })
                .fail(error => {
                    console.error('User search error', {error});
                    alert(error?.responseJSON?.data?.error || 'Error, please try again.');
                })
                .always(() => $label?.classList?.remove('busy'));
        }, 300);


    });

    // Load the form HTML via AJAX.
    let content = 'No content discovered yet.';
    jQuery.post(fetchEditFormHtmlURL, {entry_id: entryId})
        .done(result => {
            const form = result?.data?.form_html;
            content = form || 'No form discovered! Please try again.';

            if (form) {
                modal.$actions.appendChild($saveButton);
            }
        })
        .fail(error => {
            console.error('Error posting edit', error);
            content = error.responseJSON?.data?.error || 'Error, please try again.';
        })
        .always(() => {
            modal.setContent(content);
            modal.setBusy(false);
        });
}

/**
 * Setup filter columns, if the containers have any (via the [data-ams-cpd-filter-column="true"] attribute).
 *
 * @param {HTMLElement} $container The container.
 */
function setupColumnFilterBehaviour($container) {
    const $candidates = $container.querySelectorAll('[data-ams-cpd-entry-property][data-ams-cpd-filter-column="true"]');

    if ($candidates.length === 0) {
        return;
    }

    const $formTemplate = document.createElement('form');
    $formTemplate.setAttribute('method', 'get');
    $formTemplate.setAttribute('action', document.location.href);
    $formTemplate.classList.add('ams-cpd-filter-form');

    // Put query vars into hidden inputs - this will help ensure we stay on the right admin page (page=ams_cpd_entries
    // for eg), and that other filters/etc are maintained also, among whatever other query vars might exist.
    const currentUrl = new URL(document.location.href);
    currentUrl.searchParams.forEach((value, name) => {
        // Skip the page though, we do that below...
        if ('entries_page' === name) {
            return;
        }

        const $input = document.createElement('input');
        $input.setAttribute('type', 'hidden');
        $input.setAttribute('name', name);
        $input.setAttribute('value', value);
        $formTemplate.append($input);
    });

    // Make sure entries page is always 1 when doing a search.
    const $pageInput = document.createElement('input');
    $pageInput.setAttribute('type', 'hidden');
    $pageInput.setAttribute('name', 'entries_page');
    $pageInput.value = '1';

    const $searchInput = document.createElement('input');
    $searchInput.setAttribute('type', 'search');
    $searchInput.setAttribute('name', '--REPLACED--BELOW--');
    $searchInput.setAttribute('placeholder', 'Filter');
    $searchInput.classList.add('ams-cpd-filter-form__input');

    $formTemplate.append($searchInput, $pageInput);

    // For each candidate, add the form and bind filter events.
    $candidates.forEach($candidate => {
        const property = $candidate.dataset.amsCpdEntryProperty;

        if (!property) {
            return;
        }

        const inputName = `ams_cpd_filter[${property}]`;

        // Make sure the name matches the property.
        $searchInput.setAttribute('name', inputName);
        $searchInput.setAttribute('value', currentUrl.searchParams.has(inputName)
            ? currentUrl.searchParams.get(inputName)
            : ''
        );

        // Append the form to the element.
        $candidate.append($formTemplate.cloneNode(true));
    });
}


/**
 * Setup new entry creation behaviour...
 *
 * @param {HTMLElement} $container The container.
 */
function setupNewEntryBehaviour($container) {
    $container.addEventListener('click', event => {
        const $newEntryButton = event.target;
        if ($newEntryButton.tagName !== 'BUTTON' || !$newEntryButton.classList.contains('ams-cpd-entry-manager__new-entry-button')) {
            return;
        }

        event.stopImmediatePropagation();
        event.preventDefault();

        showEntryEditor(0);
    });
}


/**
 * Given a container, setup the manager, if it's right to do so...
 * @param $container
 */
function setupManagerContainer($container) {
    if (!$container.classList.contains('ams-cpd-entry-manager')) {
        return;
    }

    if ($container.hasAttribute('data-ams-cpd-entry-manager-setup')) {
        return;
    }

    $container.dataset.amsCpdEntryManagerSetup = 'true';

    setupSelectAllBehaviour($container);
    setupBulkActionBehaviour($container);
    setupRowEditBehaviour($container);
    setupColumnFilterBehaviour($container);
    setupNewEntryBehaviour($container);
}


document.addEventListener('DOMContentLoaded', () => {
    const $candidates = document.querySelectorAll('.ams-cpd-entry-manager');

    $candidates.forEach(setupManagerContainer);
});
